import { FC, useCallback, useMemo } from 'react';

import {
  GiftConversionBucket,
  GiftPacingCurvePoint,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import { useGiftPacing } from './GiftConversionPacing';
import { WEEKDAY_LABELS, fmt, fmtWonCompact } from './analysisUtils';

export type HeatMetric = 'coverage' | 'achieved' | 'fevent' | 'amount';

const HEAT_METRICS: { key: HeatMetric; label: string }[] = [
  { key: 'coverage', label: '답례글 진입 상가' },
  { key: 'achieved', label: '기대 대비 구매자' },
  { key: 'fevent', label: '상가 수' },
  { key: 'amount', label: '매출' },
];

// 발인일 통계 달력과 같은 램프 (indigo)
const HEAT_RGB = '99, 102, 241';
const DAY_MS = 24 * 60 * 60 * 1000;
/** 게이지 상한 — 150%까지 그리고 그 위는 꽉 찬 막대 */
const GAUGE_MAX = 1.5;
/** 기대가 이보다 작은 날은 비율이 튀어서 달성률을 내지 않는다 */
const MIN_EXPECTED = 0.5;

const weekdayColor = (wd: number): string =>
  wd === 0 ? 'text-red-500' : wd === 6 ? 'text-blue-500' : 'text-gray-500';

const rate = (num: number, den: number): number | null => (den > 0 ? num / den : null);

const pct = (v: number | null, digits = 0) =>
  v === null ? '—' : `${(v * 100).toFixed(digits)}%`;

/** 곡선에서 경과일 d의 누적 비중 (선형 보간) */
const cumAt = (curve: GiftPacingCurvePoint[], day: number): number => {
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (!first || !last) return 1;
  if (day <= first.day) return first.cumShare;
  if (day >= last.day) return last.cumShare;
  for (let i = 1; i < curve.length; i++) {
    const prev = curve[i - 1];
    const cur = curve[i];
    if (!prev || !cur) continue;
    if (day <= cur.day) {
      const span = cur.day - prev.day;
      const r = span === 0 ? 0 : (day - prev.day) / span;
      return prev.cumShare + (cur.cumShare - prev.cumShare) * r;
    }
  }
  return last.cumShare;
};

/** 달성률 색 — 기대의 80% 미만 경고, 110% 초과 호조 */
const achievedTone = (r: number | null) =>
  r === null
    ? { text: 'text-gray-400', bar: 'bg-gray-300' }
    : r < 0.8
    ? { text: 'text-red-600', bar: 'bg-red-500' }
    : r > 1.1
    ? { text: 'text-emerald-600', bar: 'bg-emerald-500' }
    : { text: 'text-gray-700', bar: 'bg-indigo-500' };

interface DayCell {
  /** YYYY-MM-DD (KST 발인일) */
  key: string;
  day: number;
  weekday: number;
  bucket: GiftConversionBucket | null;
  /** ① 상가 수 대비 답례글 진입자가 있는 상가 비율 */
  coverage: number | null;
  /** ①-b 번호가 있는 상주(링크가 갈 수 있는 사람) 대비 진입 비율 */
  bugoCoverage: number | null;
  /** ② 기대 구매자 대비 실제 구매자 */
  achieved: number | null;
  /** 성숙도 보정 기대치 */
  expBuyer: number | null;
  expOrder: number | null;
  expAmount: number | null;
  /** 구매 전환 성숙도 (0–1) */
  progress: number;
  /** 로그가 쌓이기 전 날 */
  beforeLog: boolean;
}

/**
 * 답례품 달력 — 발인일 하루가 칸 하나.
 *
 * 강조하는 숫자는 딱 둘이다.
 *  ① 답례글 진입 상가 비율 — 그날 발인한 상가 중 상주가 답례글에 들어온 곳이 몇 %인가
 *  ② 기대 대비 구매자 — 그 진입자면 원래 몇 명이 샀어야 하는데 실제로 몇 명이 샀나
 * 나머지(진입 명수·주문·매출)는 보조로 작게 둔다. 진입율·구매율의 절대값(1% 같은 수치)은
 * 그 자체로는 판단 근거가 못 돼서 크게 두지 않는다.
 *
 * 최근 날짜는 아직 안 익었다(답례품은 발인 +30일까지 팔린다). 그래서 기대치를
 * 성숙도(발인 후 경과일 곡선)만큼 깎아서 비교한다 — 안 그러면 최근 2~3주가 전부
 * 미달로 보인다.
 */
export const GiftConversionCalendar: FC<{
  /** interval=day 버킷 (조회 구간 전체) */
  buckets: GiftConversionBucket[];
  completeBufferDays: number;
  logStartKey: string | null;
  brandId: string;
  teamType: string;
  heatMetric: HeatMetric;
  onHeatMetricChange: (m: HeatMetric) => void;
}> = function GiftConversionCalendar({
  buckets,
  completeBufferDays,
  logStartKey,
  brandId,
  teamType,
  heatMetric,
  onHeatMetricChange,
}) {
  // 기준 진입→구매율과 성숙 곡선
  const { data: pacing } = useGiftPacing(brandId, teamType);
  const base = pacing?.baseline ?? null;
  const hasViewedBase = !!base && base.courtesyViewedBugoCount > 0;

  /** 조회 구간에 걸친 달들 (최신 달이 위로) */
  const months = useMemo(() => {
    const now = Date.now();
    const byKey = new Map(buckets.map((b) => [b.key, b]));
    const monthKeys = Array.from(new Set(buckets.map((b) => b.key.slice(0, 7)))).sort(
      (a, b) => b.localeCompare(a),
    );

    return monthKeys.map((ym) => {
      const [yRaw, mRaw] = ym.split('-').map((v: string) => parseInt(v, 10));
      const y = yRaw ?? 1970;
      const m = mRaw ?? 1;
      const lastDay = new Date(y, m, 0).getDate();
      const cells: DayCell[] = [];
      for (let day = 1; day <= lastDay; day++) {
        const key = `${ym}-${String(day).padStart(2, '0')}`;
        const b = byKey.get(key) ?? null;
        const elapsed = (now - new Date(`${key}T12:00:00+09:00`).getTime()) / DAY_MS;
        // 구매자 전환은 첫 구매 곡선, 주문·매출은 주문 곡선으로 익는다
        const pBuyer = hasViewedBase ? cumAt(base?.firstPurchaseCurve ?? [], elapsed) : 1;
        const pOrder = hasViewedBase ? cumAt(base?.orderCurve ?? [], elapsed) : 1;

        const viewedBugo = b?.courtesyViewedBugoCount ?? 0;
        const expBuyer =
          b && hasViewedBase && base
            ? viewedBugo * base.buyRatePerViewedBugo * pBuyer
            : null;
        cells.push({
          key,
          day,
          weekday: new Date(y, m - 1, day).getDay(),
          bucket: b,
          beforeLog: !!logStartKey && key < logStartKey,
          coverage: b
            ? rate(b.courtesyViewedFeventCount, b.giftEnabledFeventCount)
            : null,
          // 번호가 없으면 링크가 안 나가 진입 자체가 불가능하다 — 분모에서 뺀다
          bugoCoverage: b ? rate(b.courtesyViewedBugoCount, b.reachableBugoCount) : null,
          achieved:
            b && expBuyer !== null && expBuyer >= MIN_EXPECTED
              ? b.courtesyViewedPurchasedBugoCount / expBuyer
              : null,
          expBuyer,
          expOrder:
            b && hasViewedBase && base
              ? viewedBugo * base.ordersPerViewedBugo * pOrder
              : null,
          expAmount:
            b && hasViewedBase && base
              ? viewedBugo * base.amountPerViewedBugo * pOrder
              : null,
          progress: pBuyer,
        });
      }
      return { ym, y, m, lastDay, cells };
    });
  }, [buckets, base, hasViewedBase, logStartKey]);

  const heatValue = useCallback(
    (c: DayCell): number => {
      const b = c.bucket;
      if (!b) return 0;
      if (heatMetric === 'fevent') return b.feventCount;
      if (heatMetric === 'amount') return b.amount;
      if (heatMetric === 'coverage') return c.coverage ?? 0;
      return Math.min(c.achieved ?? 0, GAUGE_MAX);
    },
    [heatMetric],
  );
  const heatMax = useMemo(() => {
    const all = months.flatMap((mo) => mo.cells.map(heatValue));
    return Math.max(
      ...all,
      heatMetric === 'coverage' || heatMetric === 'achieved' ? 0.01 : 1,
    );
  }, [months, heatValue, heatMetric]);

  /** 툴팁은 줄 단위로 — 한 줄에 다 밀어넣으면 길어서 못 읽는다 */
  const tooltipLines = (c: DayCell): string[] => {
    const b = c.bucket;
    if (!b || b.feventCount === 0) return [`${c.key} · 발인 상가 없음`];
    return [
      `${c.key}(${WEEKDAY_LABELS[c.weekday] ?? ''})`,
      `발인 상가 ${fmt(b.feventCount)}곳 (대상 ${fmt(b.giftEnabledFeventCount)}곳)`,
      `상주 ${fmt(b.giftEnabledBugoCount)}명 · 번호 보유 ${fmt(
        b.reachableBugoCount,
      )}명 · 발송 ${fmt(b.sentBugoCount)}명`,
      c.beforeLog
        ? '조회 로그 없음'
        : `답례글 진입 상가 ${fmt(b.courtesyViewedFeventCount)}곳(${pct(
            c.coverage,
          )}) · 진입 상주 ${fmt(b.courtesyViewedBugoCount)}명`,
      c.beforeLog ? '' : `진입자 중 구매 ${fmt(b.courtesyViewedPurchasedBugoCount)}명`,
      c.expBuyer !== null
        ? `성숙 ${pct(c.progress)} — 기대 구매자 ${c.expBuyer.toFixed(
            1,
          )}명 / 주문 ${c.expOrder?.toFixed(1)}건 / 매출 ${fmt(
            Math.round(c.expAmount ?? 0),
          )}원`
        : '',
      `실제 구매 상주 ${fmt(b.purchasedBugoCount)}명 · 주문 ${fmt(
        b.orderCount,
      )}건 · ${fmt(b.amount)}원`,
    ].filter(Boolean);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          한 칸 = 발인일 하루 · 색이 진할수록{' '}
          {HEAT_METRICS.find((h) => h.key === heatMetric)?.label}이 큰 날
          {hasViewedBase && base && (
            <span className="block">
              기대 = 답례글 진입자 × 기준(구매율{' '}
              {(base.buyRatePerViewedBugo * 100).toFixed(1)}% · 주문{' '}
              {base.ordersPerViewedBugo}건 · {fmtWonCompact(base.amountPerViewedBugo)}원)
              × 성숙도 — 발인 +{completeBufferDays}일이 안 지난 날은 그만큼 기대치를
              깎는다
            </span>
          )}
          {logStartKey && (
            <span className="block">조회 로그는 {logStartKey}부터 쌓였다</span>
          )}
        </p>
        <div className="flex items-center gap-1">
          <span className="mr-1 text-sm text-gray-600">색 기준</span>
          {HEAT_METRICS.map((h) => (
            <button
              key={h.key}
              onClick={() => onHeatMetricChange(h.key)}
              className={`rounded-full border px-3 py-1 text-sm font-medium ${
                heatMetric === h.key
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>
      </div>

      {months.map((mo) => (
        <div key={mo.ym} className="rounded-lg bg-white p-4 shadow">
          <div className="mb-3">
            <h3 className="text-base font-semibold text-gray-800">
              {mo.y}년 {mo.m}월
            </h3>
            <MonthSummary cells={mo.cells} />
          </div>

          <div className="grid grid-cols-7 gap-px">
            {WEEKDAY_LABELS.map((label, wd) => (
              <div
                key={label}
                className={`px-2 py-1.5 text-center text-xs font-medium ${weekdayColor(
                  wd,
                )}`}
              >
                {label}
              </div>
            ))}
          </div>

          {/* overflow-hidden 금지 — 첫 줄 셀 툴팁이 위로 뜬다 */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 ring-1 ring-gray-200">
            {Array.from({ length: mo.cells[0]?.weekday ?? 0 }, (_, i) => (
              <div key={`lead${i}`} className="min-h-[146px] bg-gray-50" />
            ))}
            {mo.cells.map((c) => {
              const v = heatValue(c);
              return (
                <Cell
                  key={c.key}
                  cell={c}
                  alpha={v === 0 ? 0 : 0.08 + 0.62 * (v / heatMax)}
                  tooltipLines={tooltipLines(c)}
                />
              );
            })}
            {Array.from(
              {
                length: (7 - (((mo.cells[0]?.weekday ?? 0) + mo.lastDay) % 7)) % 7,
              },
              (_, i) => (
                <div key={`tail${i}`} className="min-h-[146px] bg-gray-50" />
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── 내부 컴포넌트 ───────────────────────────────────

/**
 * 기대 대비 게이지. 눈금(100% 지점)이 막대 위에 서 있어서 "선을 넘었나"로 읽힌다 —
 * 숫자만 있을 때보다 하루하루 훑을 때 훨씬 빨리 잡힌다.
 */
const ExpectGauge: FC<{ ratio: number | null; thick?: boolean }> = ({ ratio, thick }) => {
  const tone = achievedTone(ratio);
  const fill = ratio === null ? 0 : Math.min(ratio, GAUGE_MAX) / GAUGE_MAX;
  return (
    <div
      className={`relative w-full rounded-full bg-gray-200/80 ${thick ? 'h-2' : 'h-1.5'}`}
    >
      <div
        className={`h-full rounded-full ${tone.bar}`}
        style={{ width: `${Math.max(fill * 100, ratio ? 3 : 0)}%` }}
      />
      <div
        className={`absolute w-px bg-gray-500 ${
          thick ? 'top-[-3px] h-[14px]' : 'top-[-2px] h-[10px]'
        }`}
        style={{ left: `${(1 / GAUGE_MAX) * 100}%` }}
      />
    </div>
  );
};

/** 달 제목 아래 요약 — 셀과 같은 두 숫자를 크게, 기대 3종은 표로 */
const MonthSummary: FC<{ cells: DayCell[] }> = ({ cells }) => {
  const sum = cells.reduce(
    (a, c) => {
      const b = c.bucket;
      if (!b) return a;
      a.fevent += b.feventCount;
      a.target += b.giftEnabledFeventCount;
      a.enterFevent += b.courtesyViewedFeventCount;
      a.targetBugo += b.giftEnabledBugoCount;
      a.reachableBugo += b.reachableBugoCount;
      a.sentBugo += b.sentBugoCount;
      a.enterBugo += b.courtesyViewedBugoCount;
      a.storeBugo += b.storeViewedBugoCount;
      a.buy += b.courtesyViewedPurchasedBugoCount;
      a.order += b.orderCount;
      a.amount += b.amount;
      a.expBuyer += c.expBuyer ?? 0;
      a.expOrder += c.expOrder ?? 0;
      a.expAmount += c.expAmount ?? 0;
      return a;
    },
    {
      fevent: 0,
      target: 0,
      enterFevent: 0,
      targetBugo: 0,
      reachableBugo: 0,
      sentBugo: 0,
      enterBugo: 0,
      storeBugo: 0,
      buy: 0,
      order: 0,
      amount: 0,
      expBuyer: 0,
      expOrder: 0,
      expAmount: 0,
    },
  );
  if (sum.fevent === 0) return null;

  const coverage = rate(sum.enterFevent, sum.target);
  const achieved = sum.expBuyer > 0 ? sum.buy / sum.expBuyer : null;
  const tone = achievedTone(achieved);

  const rows: { label: string; exp: string; act: string; r: number | null }[] = [
    {
      label: '구매자',
      exp: `${sum.expBuyer.toFixed(1)}명`,
      act: `${fmt(sum.buy)}명`,
      r: sum.expBuyer > 0 ? sum.buy / sum.expBuyer : null,
    },
    {
      label: '주문',
      exp: `${sum.expOrder.toFixed(1)}건`,
      act: `${fmt(sum.order)}건`,
      r: sum.expOrder > 0 ? sum.order / sum.expOrder : null,
    },
    {
      label: '매출',
      exp: `${fmtWonCompact(Math.round(sum.expAmount))}원`,
      act: `${fmtWonCompact(sum.amount)}원`,
      r: sum.expAmount > 0 ? sum.amount / sum.expAmount : null,
    },
  ];

  return (
    <div className="mt-2 flex flex-wrap items-start gap-x-8 gap-y-3">
      <div className="min-w-[180px]">
        <p className="text-sm text-gray-700">답례글 진입 상가</p>
        <p className="text-3xl font-bold leading-none text-indigo-700">
          {pct(coverage, 1)}
        </p>
        <p className="mt-1 text-sm text-gray-700">
          {fmt(sum.enterFevent)}곳 / 대상 {fmt(sum.target)}곳 (전체 {fmt(sum.fevent)}곳)
        </p>
      </div>

      <div className="min-w-[170px]">
        <p className="text-sm text-gray-700">답례글 진입 상주</p>
        <p className="text-3xl font-bold leading-none text-sky-700">
          {pct(rate(sum.enterBugo, sum.targetBugo), 1)}
        </p>
        <p className="mt-1 text-sm text-gray-700">
          {fmt(sum.enterBugo)}명 / 대상 {fmt(sum.targetBugo)}명 · 스토어{' '}
          {fmt(sum.storeBugo)}명
        </p>
      </div>

      <div className="min-w-[210px]">
        <p className="text-sm text-gray-700">기대 구매자 대비 실구매자</p>
        <p className={`text-3xl font-bold leading-none ${tone.text}`}>
          {achieved === null ? '—' : `${Math.round(achieved * 100)}%`}
        </p>
        <div className="mt-1.5">
          <ExpectGauge ratio={achieved} thick />
        </div>
        <p className="mt-1 text-sm text-gray-700">
          기대 {sum.expBuyer.toFixed(1)}명 → 실제 {fmt(sum.buy)}명 (눈금 = 기대치)
        </p>
      </div>

      <table className="text-sm text-gray-800">
        <thead className="text-gray-600">
          <tr>
            <th className="pr-3 text-left font-medium">항목</th>
            <th className="pr-3 text-right font-medium">기대</th>
            <th className="pr-3 text-right font-medium">실제</th>
            <th className="text-right font-medium">달성</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td className="pr-3">{r.label}</td>
              <td className="pr-3 text-right">{r.exp}</td>
              <td className="pr-3 text-right font-semibold text-gray-800">{r.act}</td>
              <td className={`text-right font-semibold ${achievedTone(r.r).text}`}>
                {r.r === null ? '—' : `${Math.round(r.r * 100)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** 기대 · 실제 · 기대비 한 줄 (셀 안의 4열 그리드에 그대로 박힌다) */
const ExpectRow: FC<{
  label: string;
  expected: number | null;
  actual: number;
  fmtValue: (v: number) => string;
}> = ({ label, expected, actual, fmtValue }) => {
  // 기대가 너무 작으면 비율이 튄다 — 값은 보여주되 대비는 생략
  const r = expected !== null && expected >= 0.5 ? actual / expected : null;
  return (
    <>
      <span className="text-gray-600">{label}</span>
      <span className="text-right text-gray-700">
        {expected !== null ? fmtValue(expected) : '—'}
      </span>
      <span className="text-right font-semibold text-gray-900">{fmtValue(actual)}</span>
      <span className={`text-right font-semibold ${achievedTone(r).text}`}>
        {r === null ? '—' : `${Math.round(r * 100)}%`}
      </span>
    </>
  );
};

const Cell: FC<{
  cell: DayCell;
  alpha: number;
  tooltipLines: string[];
}> = ({ cell: c, alpha, tooltipLines }) => {
  const b = c.bucket;
  const tone = achievedTone(c.achieved);

  return (
    <div
      className="group relative min-h-[146px] bg-white px-1.5 pt-1 pb-1"
      style={alpha > 0 ? { backgroundColor: `rgba(${HEAT_RGB}, ${alpha})` } : undefined}
    >
      {/* 툴팁 — 줄바꿈해서 크게. 첫 줄은 날짜라 굵게 */}
      <div className="pointer-events-none absolute bottom-full left-1/2 z-20 hidden w-max max-w-[320px] -translate-x-1/2 space-y-0.5 rounded-md bg-gray-900/95 px-3 py-2 text-sm leading-snug text-white shadow-lg group-hover:block">
        {tooltipLines.map((line, i) => (
          <p key={line} className={i === 0 ? 'font-semibold' : 'text-gray-200'}>
            {line}
          </p>
        ))}
      </div>

      <p className="text-sm font-semibold text-gray-800">{c.day}</p>

      {b && b.feventCount > 0 && (
        <div className="mt-0.5">
          {/* 강조 셋 — ① 진입 상가 ② 진입 상주 ③ 기대 대비 구매자 */}
          <div className="flex items-start justify-between gap-0.5">
            <div>
              <p className="text-base font-bold leading-none text-indigo-700">
                {c.beforeLog ? '—' : pct(c.coverage)}
              </p>
              <p className="mt-0.5 text-xs leading-none text-gray-700">
                {fmt(b.courtesyViewedFeventCount)}/{fmt(b.giftEnabledFeventCount)}곳
              </p>
            </div>
            <div className="text-center">
              <p className="text-base font-bold leading-none text-sky-700">
                {c.beforeLog ? '—' : pct(c.bugoCoverage)}
              </p>
              <p className="mt-0.5 text-xs leading-none text-gray-700">
                {fmt(b.courtesyViewedBugoCount)}/{fmt(b.reachableBugoCount)}명
              </p>
            </div>
            <div className="text-right">
              <p className={`text-base font-bold leading-none ${tone.text}`}>
                {c.achieved === null ? '—' : `${Math.round(c.achieved * 100)}%`}
              </p>
              <p className="mt-0.5 text-xs leading-none text-gray-700">
                구매 {fmt(b.courtesyViewedPurchasedBugoCount)}명
              </p>
            </div>
          </div>

          {!c.beforeLog && (
            <p className="mt-0.5 text-xs leading-tight text-gray-700">
              스토어 진입{' '}
              <span className="font-semibold text-gray-900">
                {fmt(b.storeViewedBugoCount)}
              </span>
              명
            </p>
          )}

          {c.achieved !== null && (
            <div className="mt-0.5">
              <ExpectGauge ratio={c.achieved} />
            </div>
          )}

          {/* 기대 ↔ 실제 ↔ 기대비 — 항목별로 나란히 놓아야 어디가 벌어졌는지 바로 보인다 */}
          <div className="mt-0.5 grid grid-cols-[auto_1fr_1fr_1fr] gap-x-1 text-xs leading-tight">
            <span />
            <span className="text-right text-gray-500">기대</span>
            <span className="text-right text-gray-500">실제</span>
            <span className="text-right text-gray-500">대비</span>

            <ExpectRow
              label="명"
              expected={c.expBuyer}
              actual={b.courtesyViewedPurchasedBugoCount}
              fmtValue={(v) => v.toFixed(1)}
            />
            <ExpectRow
              label="건"
              expected={c.expOrder}
              actual={b.orderCount}
              fmtValue={(v) => v.toFixed(1)}
            />
            <ExpectRow
              label="액"
              expected={c.expAmount}
              actual={b.amount}
              fmtValue={(v) => fmtWonCompact(Math.round(v))}
            />
          </div>

          <p className="mt-0.5 text-xs leading-tight text-gray-700">
            상가 {fmt(b.feventCount)}곳 · 구매 상주 {fmt(b.purchasedBugoCount)}명
          </p>
        </div>
      )}
    </div>
  );
};
